import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  createJob,
  getEntityTypes,
  listIndexes,
  type AiSummaryMode,
  type ReindexEntityType,
  type ReindexMode,
} from '@/services/reindex-api';
import { AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';

const TARGET_INDEX_REGEX = /^[a-z][a-z0-9_]*$/;

// Rough heuristic counts for cost-estimate banner.
const FULL_ESTIMATES: Record<ReindexEntityType, number> = {
  person: 250_000,
  company: 210_000,
  opportunity: 0, // unknown — we'll show "?"
  note: 0,
  list: 0,
  listEntry: 0,
};

// Throughput tuning targets (docs/hr) — feeds the wall-clock estimate.
const THROUGHPUT_AI_ENABLED = 600;
const COST_PER_DOC_AI_ENABLED_USD = 0.0025;

export function ReindexJobNew() {
  const navigate = useNavigate();

  // Form state
  const [entityType, setEntityType] = useState<ReindexEntityType | ''>('');
  const [mode, setMode] = useState<ReindexMode>('delta');
  const [idSource, setIdSource] = useState<'affinity' | 'snowflake'>('snowflake');
  const [aiSummaryMode, setAiSummaryMode] = useState<AiSummaryMode>('auto');
  const [indexSelection, setIndexSelection] = useState<string>(''); // dropdown value
  const [customIndex, setCustomIndex] = useState<string>('');
  const [confirmCheckbox, setConfirmCheckbox] = useState(false);
  const [confirmEntityName, setConfirmEntityName] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Server data: entity types + available OS indices
  const { data: entityTypes } = useQuery({
    queryKey: ['entity-types'],
    queryFn: getEntityTypes,
  });

  const { data: indexes, isLoading: indexesLoading } = useQuery({
    queryKey: ['indexes', entityType],
    queryFn: () => listIndexes(entityType || undefined),
    enabled: Boolean(entityType),
  });

  const targetIndex = indexSelection === '__custom__' ? customIndex : indexSelection;
  const targetIndexValid = targetIndex && TARGET_INDEX_REGEX.test(targetIndex);

  // Cost / time estimate
  const estimate = useMemo(() => {
    if (!entityType) return null;
    if (mode === 'delta') {
      return {
        count: null, // computed during preflight
        costUsd: null,
        hours: null,
        unknownReason: 'Computed during delta preflight',
      };
    }
    const count = FULL_ESTIMATES[entityType] || 0;
    if (count === 0) {
      return {
        count: null,
        costUsd: null,
        hours: null,
        unknownReason: 'Unknown — Affinity workspace size varies',
      };
    }
    // AI-summary mode drives Anthropic cost: 'reuse' regenerates nothing ($0);
    // 'force' regenerates everything (full estimate); 'auto' is somewhere
    // between (depends how many are within the TTL) — show the upper bound.
    const costUsd =
      aiSummaryMode === 'reuse' ? 0 : count * COST_PER_DOC_AI_ENABLED_USD;
    return {
      count,
      costUsd,
      costIsUpperBound: aiSummaryMode === 'auto',
      hours: count / THROUGHPUT_AI_ENABLED,
      unknownReason: null,
    };
  }, [entityType, mode, aiSummaryMode]);

  const costRequiresWarning = (estimate?.costUsd ?? 0) > 100 || (estimate?.hours ?? 0) > 24;
  const costRequiresTypedConfirm = (estimate?.costUsd ?? 0) > 50;

  // Empty-index warning
  const selectedIndexInfo = indexes?.indices.find((i) => i.name === targetIndex);
  const deltaAgainstEmpty =
    mode === 'delta' &&
    selectedIndexInfo &&
    selectedIndexInfo.docsCount === 0;

  // Submission validity
  const canSubmit =
    entityType !== '' &&
    targetIndexValid &&
    (!costRequiresWarning || confirmCheckbox) &&
    !submitting;

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await createJob({
        entityType: entityType as ReindexEntityType,
        targetIndex: targetIndex!,
        mode,
        idSource,
        aiSummaryMode,
      });
      navigate(`/reindex/${result.jobId}`);
    } catch (err) {
      setSubmitError((err as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/reindex')} className="gap-2">
        <ArrowLeft className="h-4 w-4" /> Back to jobs
      </Button>

      <div>
        <h2 className="text-3xl font-bold tracking-tight">New Reindex</h2>
        <p className="text-muted-foreground">
          Backfill or refresh entities. Pulls IDs from Affinity (or Snowflake),
          posts them to the webhook pipeline; the Railway workers do the AI
          summary (subject to the AI-summary option), embedding, and indexing.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Entity type */}
          <div className="grid gap-2">
            <Label htmlFor="entityType">Entity type</Label>
            <Select value={entityType} onValueChange={(v) => {
              setEntityType(v as ReindexEntityType);
              setIndexSelection('');
              setCustomIndex('');
            }}>
              <SelectTrigger id="entityType" className="w-full max-w-md">
                <SelectValue placeholder="Choose an entity type" />
              </SelectTrigger>
              <SelectContent>
                {entityTypes?.enabled.map((e) => (
                  <SelectItem key={e.key} value={e.key}>
                    {e.displayName}
                  </SelectItem>
                ))}
                {entityTypes?.disabled.map((d) => (
                  <TooltipProvider key={d.key}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <SelectItem value={d.key} disabled>
                            {d.displayName} (unavailable)
                          </SelectItem>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-md">
                        {d.reason}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Mode */}
          <div className="grid gap-2">
            <Label>Reindex mode</Label>
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as ReindexMode)}
              className="space-y-2"
            >
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4 hover:bg-accent">
                <RadioGroupItem value="delta" id="mode-delta" className="mt-1" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Delta</span>
                    <Badge variant="secondary">recommended</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Only reindex Affinity entities missing from the target index.
                    Common case for catching up after live webhook drift. Saves
                    AI cost proportional to gap size.
                  </p>
                </div>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4 hover:bg-accent">
                <RadioGroupItem value="full" id="mode-full" className="mt-1" />
                <div>
                  <span className="text-sm font-semibold">Full</span>
                  <p className="text-sm text-muted-foreground">
                    Reindex every Affinity entity, overwriting existing documents.
                    Use for new versioned indexes or schema changes. Whether the
                    LLM summary is reused or regenerated is governed by the{' '}
                    <strong>AI summary</strong> option below.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* ID source */}
          <div className="grid gap-2">
            <Label>Entity ID source</Label>
            <RadioGroup
              value={idSource}
              onValueChange={(v) => setIdSource(v as 'affinity' | 'snowflake')}
              className="space-y-2"
            >
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4 hover:bg-accent">
                <RadioGroupItem value="snowflake" id="source-snowflake" className="mt-1" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Snowflake</span>
                    <Badge variant="secondary">recommended</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Reads entity IDs from the ANTHOS_DWH.MARTS.AFFINITY_* mart
                    tables in a single streaming query. Orders of magnitude
                    faster than the Affinity API walk; no rate-limit overhead.
                    Per-doc data still fetched from Affinity.
                  </p>
                </div>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4 hover:bg-accent">
                <RadioGroupItem value="affinity" id="source-affinity" className="mt-1" />
                <div>
                  <span className="text-sm font-semibold">Affinity v2 API</span>
                  <p className="text-sm text-muted-foreground">
                    Paginated walk through the Affinity v2 API. Slower but
                    real-time accurate. Use if Snowflake credentials are
                    unavailable or you suspect mart staleness.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* AI summary reuse policy */}
          <div className="grid gap-2">
            <Label>AI summary</Label>
            <RadioGroup
              value={aiSummaryMode}
              onValueChange={(v) => setAiSummaryMode(v as AiSummaryMode)}
              className="space-y-2"
            >
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4 hover:bg-accent">
                <RadioGroupItem value="auto" id="ai-auto" className="mt-1" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Auto</span>
                    <Badge variant="secondary">recommended</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Reuse the existing summary when it's within the freshness
                    window (AI_SUMMARY_TTL_DAYS, default 60), otherwise regenerate.
                    Re-embeds either way. Skips Anthropic cost for recently-summarized docs.
                  </p>
                </div>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4 hover:bg-accent">
                <RadioGroupItem value="reuse" id="ai-reuse" className="mt-1" />
                <div>
                  <span className="text-sm font-semibold">Reuse (no Anthropic)</span>
                  <p className="text-sm text-muted-foreground">
                    Always reuse an existing summary regardless of age — zero
                    Anthropic cost. Docs with no existing summary still get one.
                    Best for data-only field changes (e.g. region) on an index
                    that already has summaries.
                  </p>
                </div>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4 hover:bg-accent">
                <RadioGroupItem value="force" id="ai-force" className="mt-1" />
                <div>
                  <span className="text-sm font-semibold">Force regenerate</span>
                  <p className="text-sm text-muted-foreground">
                    Regenerate every summary via Anthropic, ignoring any cached
                    value. Most expensive — use for prompt changes or a new
                    versioned index where you want fresh summaries.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Target index */}
          <div className="grid gap-2">
            <Label htmlFor="targetIndex">Target OpenSearch index</Label>
            {!entityType ? (
              <p className="text-sm text-muted-foreground">
                Pick an entity type first.
              </p>
            ) : indexesLoading ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading indexes…
              </div>
            ) : (
              <>
                <Select
                  value={indexSelection}
                  onValueChange={(v) => setIndexSelection(v)}
                >
                  <SelectTrigger id="targetIndex" className="w-full max-w-md">
                    <SelectValue placeholder="Choose an index" />
                  </SelectTrigger>
                  <SelectContent>
                    {indexes?.indices.map((i) => (
                      <SelectItem key={i.name} value={i.name}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">{i.name}</span>
                          {i.isAliasTarget && (
                            <Badge variant="default" className="text-[10px]">
                              ACTIVE
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {i.docsCount.toLocaleString()} docs
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom__">
                      Advanced: enter index name…
                    </SelectItem>
                  </SelectContent>
                </Select>
                {indexSelection === '__custom__' && (
                  <Input
                    placeholder="e.g. companies_v12"
                    value={customIndex}
                    onChange={(e) => setCustomIndex(e.target.value)}
                    className="max-w-md font-mono"
                  />
                )}
                {targetIndex && !targetIndexValid && (
                  <p className="text-sm text-destructive">
                    Index name must be lowercase alphanumeric with underscores
                    only ([a-z][a-z0-9_]*).
                  </p>
                )}
                {deltaAgainstEmpty && (
                  <div className="flex items-start gap-2 rounded-md bg-yellow-50 dark:bg-yellow-950/30 p-3 text-sm">
                    <AlertTriangle className="mt-0.5 h-4 w-4 text-yellow-600" />
                    <span>
                      Delta against an empty index is equivalent to Full. Consider
                      switching to Full mode.
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Cost estimate */}
          {estimate && (
            <Card className={costRequiresWarning ? 'border-yellow-400' : ''}>
              <CardHeader>
                <CardTitle className="text-base">Estimated cost & time</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {estimate.unknownReason ? (
                  <p className="text-muted-foreground">{estimate.unknownReason}</p>
                ) : (
                  <>
                    <p>
                      Approximate document count:{' '}
                      <strong>{estimate.count?.toLocaleString()}</strong>
                    </p>
                    <p>
                      Estimated Anthropic cost:{' '}
                      <strong>
                        {estimate.costIsUpperBound ? '≤ ' : ''}$
                        {estimate.costUsd?.toFixed(2)}
                        {estimate.costUsd ? ` at $${COST_PER_DOC_AI_ENABLED_USD}/doc` : ' (summaries reused)'}
                      </strong>
                      {estimate.costIsUpperBound && (
                        <span className="text-muted-foreground">
                          {' '}— auto reuse lowers this for docs summarized in the last 60 days
                        </span>
                      )}
                    </p>
                    <p>
                      Estimated wall-clock time:{' '}
                      <strong>{estimate.hours?.toFixed(1)} hours</strong>
                    </p>
                    {costRequiresWarning && (
                      <div className="rounded-md bg-yellow-50 dark:bg-yellow-950/30 p-3 mt-2">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="mt-0.5 h-4 w-4 text-yellow-600" />
                          <div className="space-y-2">
                            <p>
                              <strong>This job is expensive and long-running.</strong>{' '}
                              Make sure OpenSearch has been scaled up before kicking it off.
                            </p>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={confirmCheckbox}
                                onCheckedChange={(c) => setConfirmCheckbox(Boolean(c))}
                              />
                              <span>I understand this is expensive and long-running.</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          {submitError && (
            <p className="text-sm text-destructive">Error: {submitError}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => navigate('/reindex')}>
              Cancel
            </Button>
            <Button
              disabled={!canSubmit}
              onClick={() => setShowConfirmDialog(true)}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === 'delta' ? 'Start preflight' : 'Start reindex'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm reindex</DialogTitle>
            <DialogDescription>
              You are about to start a {mode === 'delta' ? 'delta-mode' : 'full-mode'} reindex.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              <strong>Entity type:</strong> {entityType}
            </p>
            <p>
              <strong>Target index:</strong>{' '}
              <span className="font-mono">{targetIndex}</span>
            </p>
            <p>
              <strong>Mode:</strong> {mode}
            </p>
            <p>
              <strong>AI summary:</strong> {aiSummaryMode}
            </p>
            {estimate?.costUsd != null && (
              <p>
                <strong>Estimated cost:</strong> ${estimate.costUsd.toFixed(2)}
              </p>
            )}
          </div>
          {costRequiresTypedConfirm && (
            <div className="space-y-2">
              <Label htmlFor="confirmName">
                Type <span className="font-mono">{entityType}</span> to confirm
              </Label>
              <Input
                id="confirmName"
                value={confirmEntityName}
                onChange={(e) => setConfirmEntityName(e.target.value)}
                className="max-w-xs"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                submitting ||
                (costRequiresTypedConfirm && confirmEntityName !== entityType)
              }
              onClick={handleSubmit}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
